package br.com.vvrefeicoes.portal.core.util.spreadsheet

/**
 * Leitor mínimo de arquivos ZIP (usado para abrir o `.xlsx`). Suporta entradas armazenadas
 * (`stored`) e comprimidas com DEFLATE, usando o [Inflate] em Kotlin. Sem dependências.
 */
internal object ZipReader {
    private const val EOCD_SIGNATURE = 0x06054b50L
    private const val CENTRAL_SIGNATURE = 0x02014b50L
    private const val LOCAL_SIGNATURE = 0x04034b50L

    fun read(data: ByteArray): Map<String, ByteArray> {
        val eocd = findEndOfCentralDirectory(data)
        val totalEntries = readU16(data, eocd + 10)
        val centralOffset = readU32(data, eocd + 16).toInt()
        val entries = LinkedHashMap<String, ByteArray>(totalEntries)
        var pointer = centralOffset
        repeat(totalEntries) {
            if (readU32(data, pointer) != CENTRAL_SIGNATURE) {
                throw SpreadsheetFormatException("ZIP inválido: diretório central corrompido")
            }
            val method = readU16(data, pointer + 10)
            val compressedSize = readU32(data, pointer + 20).toInt()
            val nameLength = readU16(data, pointer + 28)
            val extraLength = readU16(data, pointer + 30)
            val commentLength = readU16(data, pointer + 32)
            val localOffset = readU32(data, pointer + 42).toInt()
            val name = data.decodeToString(pointer + 46, pointer + 46 + nameLength)
            entries[name] = extractEntry(data, localOffset, method, compressedSize)
            pointer += 46 + nameLength + extraLength + commentLength
        }
        return entries
    }

    private fun extractEntry(
        data: ByteArray,
        localOffset: Int,
        method: Int,
        compressedSize: Int,
    ): ByteArray {
        if (readU32(data, localOffset) != LOCAL_SIGNATURE) {
            throw SpreadsheetFormatException("ZIP inválido: cabeçalho local corrompido")
        }
        val nameLength = readU16(data, localOffset + 26)
        val extraLength = readU16(data, localOffset + 28)
        val start = localOffset + 30 + nameLength + extraLength
        if (start + compressedSize > data.size) {
            throw SpreadsheetFormatException("ZIP inválido: dados truncados")
        }
        val compressed = data.copyOfRange(start, start + compressedSize)
        return when (method) {
            0 -> compressed
            8 -> Inflate.inflate(compressed)
            else -> throw SpreadsheetFormatException("Método de compressão ZIP não suportado: $method")
        }
    }

    private fun findEndOfCentralDirectory(data: ByteArray): Int {
        val minimum = maxOf(0, data.size - 65557)
        var offset = data.size - 22
        while (offset >= minimum) {
            if (readU32(data, offset) == EOCD_SIGNATURE) return offset
            offset--
        }
        throw SpreadsheetFormatException("ZIP inválido: fim do diretório central não encontrado")
    }

    private fun readU16(
        data: ByteArray,
        offset: Int,
    ): Int = (data[offset].toInt() and 0xFF) or ((data[offset + 1].toInt() and 0xFF) shl 8)

    private fun readU32(
        data: ByteArray,
        offset: Int,
    ): Long {
        var value = 0L
        for (i in 0 until 4) value = value or ((data[offset + i].toInt() and 0xFF).toLong() shl (8 * i))
        return value
    }
}
